import {
  ArrowUpRight,
  Ellipsis,
  FileText,
  FolderOpen,
  Menu,
  Shapes,
  ShieldCheck,
  UploadCloud,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import RewardCoin from '@/components/Icons/RewardCoin';
import filefinityMark from '@/assets/filefinity-logo.png';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import ProfilePicture from '../ui/profile-picture';
import { IUser } from '@/redux/auth/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { LoginButton } from './LoginDialog';
import { FeedRowContent } from './FeedLink';
import RewardsProgramTermsModal from '@/components/RewardsProgramTermsModal';
import { cn } from '@/lib/utils';
import { setShowFeedSheet } from '@/redux/global/global.slice';

const ROW =
  'h-12 w-full justify-start gap-3 rounded-lg px-2 text-base font-medium';

/** The reward coin is a colour illustration; a tile lets it sit with the
 *  line icons instead of reading as a stray sticker. */
const TILE =
  'flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-muted text-muted-foreground';

interface MobileNavProps {
  isAuthenticated: boolean;
  user: IUser | null;
  onLogout: () => void;
}

function MobileNav({ isAuthenticated, user, onLogout }: MobileNavProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const dispatch = useDispatch();
  // Controlled so the rewards modal can live OUTSIDE the Sheet; otherwise
  // closing the sheet on click unmounts the dialog the same click opened.
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const ADMIN_DASHBOARD_URL = import.meta.env.VITE_ADMIN_DASHBOARD_URL;

  return (
    <>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-heading lg:hidden"
            aria-label="Open navigation menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent className="flex w-full flex-col p-0 sm:w-[400px]">
          <SheetHeader className="border-b p-4 pr-12 text-left">
            <div className="flex items-center gap-2.5">
              <img
                src={filefinityMark}
                alt=""
                className="h-8 w-auto object-contain"
              />
              <span className="leading-tight">
                <span className="flex items-center gap-1.5">
                  <SheetTitle className="text-lg font-bold text-heading">
                    Filefinity
                  </SheetTitle>
                  <span className="rounded-full bg-blue-50 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-blue-600">
                    Beta
                  </span>
                </span>
                <SheetDescription className="text-[11px]">
                  One site, infinite CNC files
                </SheetDescription>
              </span>
            </div>
          </SheetHeader>

          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
            <Button asChild variant="ghost" className={ROW}>
              <Link to="/blog" onClick={() => setSheetOpen(false)}>
                <span className={TILE}>
                  <FileText className="h-[18px] w-[18px]" />
                </span>
                Blog
              </Link>
            </Button>

            {isAuthenticated && (
              <Button
                variant="ghost"
                className={ROW}
                aria-label="Feed"
                aria-haspopup="dialog"
                onClick={() => {
                  setSheetOpen(false);
                  dispatch(setShowFeedSheet(true));
                }}
              >
                <FeedRowContent tileClassName={TILE} />
              </Button>
            )}

            <Button
              variant="ghost"
              className={cn(ROW, 'h-auto py-2.5')}
              onClick={() => {
                setSheetOpen(false);
                setRewardsOpen(true);
              }}
            >
              <span className={TILE}>
                <RewardCoin coinType="gold" width={20} height={20} />
              </span>
              <span className="flex flex-col items-start leading-tight">
                Upload &amp; Earn
                <span className="text-xs font-normal text-muted-foreground">
                  Earn Onefinity store credit
                </span>
              </span>
            </Button>

            {isAuthenticated && (
              <Button asChild variant="ghost" className={ROW}>
                <Link
                  to={`/${user?.username}`}
                  onClick={() => setSheetOpen(false)}
                >
                  <span className={TILE}>
                    <FolderOpen className="h-[18px] w-[18px]" />
                  </span>
                  My Files
                </Link>
              </Button>
            )}

            <p className="px-2 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              More
            </p>
            <Button asChild variant="ghost" className={cn(ROW, 'h-10 text-sm')}>
              <Link to="/legend" onClick={() => setSheetOpen(false)}>
                <span className={cn(TILE, 'h-8 w-8')}>
                  <Shapes className="h-4 w-4" />
                </span>
                Icon Legend
              </Link>
            </Button>
            <Button asChild variant="ghost" className={cn(ROW, 'h-10 text-sm')}>
              <Link to="/privacyPolicy" onClick={() => setSheetOpen(false)}>
                <span className={cn(TILE, 'h-8 w-8')}>
                  <ShieldCheck className="h-4 w-4" />
                </span>
                Privacy Policy
              </Link>
            </Button>
          </nav>

          <SheetFooter className="flex-col gap-3 border-t p-4 sm:flex-col sm:space-x-0">
            {isAuthenticated ? (
              <Button asChild size="lg" className="w-full gap-2">
                <Link
                  to="/project/v2/upload"
                  onClick={() => setSheetOpen(false)}
                >
                  <UploadCloud className="h-5 w-5" />
                  Upload
                </Link>
              </Button>
            ) : (
              <>
                <LoginButton variant="default" className="w-full" />
                <Button asChild variant="outline" className="w-full">
                  <Link to="/signup" onClick={() => setSheetOpen(false)}>
                    Sign up
                  </Link>
                </Button>
              </>
            )}

            {isAuthenticated && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <Link
                  to={`/${user?.username}`}
                  onClick={() => setSheetOpen(false)}
                  className="flex min-w-0 items-center gap-3"
                >
                  <ProfilePicture
                    src={user?.profilePicture}
                    alt={user?.firstname}
                    className="h-10 w-10 border-2"
                    points={user?.currentPoints}
                    tier={user?.tier}
                    variant="pill"
                  />
                  <p className="truncate font-semibold">@{user?.username}</p>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Account menu"
                    >
                      <Ellipsis className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56"
                    onClick={() => setSheetOpen(false)}
                  >
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild>
                        <Link to={`/${user?.username}`}>Profile</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/changePassword">Change Password</Link>
                      </DropdownMenuItem>
                      {user?.isAdmin && (
                        <DropdownMenuItem asChild>
                          <a
                            href={ADMIN_DASHBOARD_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between"
                          >
                            Admin Dashboard
                            <ArrowUpRight className="h-4 w-4" />
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={onLogout}>
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <a
              href="https://www.onefinitycnc.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Powered by Onefinity
            </a>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {/* Rendered outside the Sheet so closing the sheet doesn't unmount it. */}
      <RewardsProgramTermsModal
        open={rewardsOpen}
        onOpenChange={setRewardsOpen}
      />
    </>
  );
}

export default MobileNav;
